import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Spinner } from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import Cookies from 'js-cookie';

import { TFeedItem } from 'homepage-web-bff/types/TFeeds';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';

import FeedItem from '../../../Homepage/components/Feeds/FeedItem';
import convertSnakeToPascalCase from '../../../Homepage/components/Feeds/utils/convertSnakeToPascalCase';
import getFeedItems from '../../../Homepage/repositories/getFeedItems';

import { trackGAEvent, trackGAEventOnce } from '../../../../utils/gaTracking';
import { categories, gaParams } from '../constants/analytics';
import AutosFeedsListSkeleton from './AutosFeedsListSkeleton';

import Classes from './AutosFeedsList.module.scss';

type TAutosFeedsList = {
  setShowLoginDialog: Function;
};

const FEED_TYPE = 'CAR_RECOMMENDATIONS';
const FIRST_VISIBLE_ITEMS = 2;

const FEED = {
  feedType: FEED_TYPE,
  displayTitle: '',
  locationRequired: false,
  loginRequired: true,
};

const isInFirstVisibleViewport = (index: number) => index < FIRST_VISIBLE_ITEMS;

const AutosFeedsList: React.FC<TAutosFeedsList> = ({ setShowLoginDialog }) => {
  const { t } = useI18nContext();
  const { userLoggedIn, xsrfToken } = useContext(EnvironmentContext);

  const [items, setItems] = useState<TFeedItem[]>([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  // Use a ref for the page number to avoid stale closures inside the onLoadMore callback
  const pageRef = useRef(0);

  const fetchItems = useCallback(
    async (page: number) => {
      setIsLoading(true);

      try {
        const feedItems = await getFeedItems({ feed: FEED, page }, userLoggedIn, xsrfToken);
        const hasItems = feedItems.length > 0;

        if (hasItems) {
          setItems((prev) => [...prev, ...feedItems]);
        }
        setHasNextPage(hasItems);

        if (page === 0) {
          const label = convertSnakeToPascalCase(FEED_TYPE);
          if (hasItems) {
            trackGAEvent('Displayed', label, { [gaParams.pageType]: categories.DISCOVER });
            trackGAEventOnce('FirstLoadNotEmpty', label, { [gaParams.pageType]: categories.DISCOVER });
          } else {
            trackGAEvent('EmptyStateDisplayed', label, { [gaParams.pageType]: categories.DISCOVER });
          }
        }
      } catch {
        setHasNextPage(false);
      } finally {
        setIsLoading(false);
      }
    },
    [userLoggedIn, xsrfToken],
  );

  useEffect(() => {
    void fetchItems(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemClick = useCallback((correlationId) => {
    const label = convertSnakeToPascalCase(FEED_TYPE);
    Cookies.set('searchRequestId', correlationId);
    trackGAEvent('Clicked', label, { [gaParams.pageType]: categories.DISCOVER });
  }, []);

  const handleLoadMore = useCallback(
    (pageIncrement = 1) => {
      pageRef.current = pageRef.current + pageIncrement;
      const label = convertSnakeToPascalCase(FEED_TYPE);
      trackGAEvent(`LoadMore${pageRef.current}`, label, { [gaParams.pageType]: categories.DISCOVER });
      void fetchItems(pageRef.current);
    },
    [fetchItems],
  );

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: handleLoadMore,
    rootMargin: '0px 0px 1000px 0px',
    delayInMs: 100,
  });

  if (!items.length && !isLoading) {
    return <Trans className={Classes.message} i18nKey="homepage.feeds.empty" />;
  }

  if (!items.length) {
    return (
      <div className={Classes.container}>
        <AutosFeedsListSkeleton />
      </div>
    );
  }

  return (
    <div className={Classes.container}>
      <Box as="div" name="contentCards" className={Classes.contentCards}>
        {items.map((item, i) => (
          <FeedItem
            className={Classes.autoFeedItem}
            key={`${item.itemId}-${i}`}
            item={item}
            lazyLoadImage={!isInFirstVisibleViewport(i)}
            onClick={handleItemClick}
            feedType={FEED_TYPE}
            setShowLoginDialog={setShowLoginDialog}
          />
        ))}
      </Box>
      {hasNextPage && <div ref={sentryRef}>{isLoading ? <Spinner /> : <AutosFeedsListSkeleton />}</div>}
      {!hasNextPage && (
        <div className={Classes.loadmore}>
          <Button.Secondary onClick={() => handleLoadMore(0)} disabled={isLoading}>
            {t('homepage.feeds.load_more')}
          </Button.Secondary>
        </div>
      )}
    </div>
  );
};

export default AutosFeedsList;
